import React, { useState, useEffect, Suspense } from "react";
import GoBackButton from "shared/ui/back-button";
import "./style.scss";
import Button from "shared/ui/button";
import { PATH_LIST } from "shared/lib/react-router";
import { useNavigate } from "react-router-dom";
import { onboardModel } from "entities/onboard";
import {
  RegisterEnum,
  onboardStore,
} from "entities/onboard/model/onboardModel";
import { useStore } from "zustand";
import { ResponseCodes, sircapApi } from "shared/api/sircap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import TermsCheckbox from "pages/onboarding/components/terms-checkbox";
import { sessionModel } from "entities/session";
import IndividualBusinessToggle from "widgets/account-type";

const LazyPhoneInput = React.lazy(() =>
  import("react-international-phone").then((mod) => ({
    default: mod.PhoneInput,
  }))
);

export const ForgotPasswordPage: React.FC = () => {
  const [phone, setPhone] = useState<string>("");
  const { setPhoneNumber } = useStore(onboardStore);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { i18n } = useTranslation();
  const { t } = useTranslation("onboarding-auth");
  const { t: y } = useTranslation("yup");
  const { t: err } = useTranslation("errors");

  const { setLink } = useStore(sessionModel.sessionStore);
  const navigate = useNavigate();
  const { setRegisterType } = useStore(onboardModel.onboardStore);
  const [isEntity, setIsEntity] = useState(false);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const validatePhone = async () => {
      if (!phone) return setIsValid(false);

      try {
        const { PhoneNumberUtil } = await import("google-libphonenumber");
        const phoneUtil = PhoneNumberUtil.getInstance();
        const parsed = phoneUtil.parseAndKeepRawInput(phone);
        if (isMounted) {
          setIsValid(phoneUtil.isValidNumber(parsed));
        }
      } catch {
        setIsValid(false);
      }
    };

    validatePhone();

    return () => {
      isMounted = false;
    };
  }, [phone]);

  useEffect(() => {
    setLink(PATH_LIST.loginPassword);
    return () => setLink("");
  }, []);

  const onRegister = async () => {
    setIsSubmitting(true);
    const clearPhone = phone.replace(/\D/g, "");

    const resp = await sircapApi.auth.forgot({
      phone: clearPhone,
      language: i18n.language,
      entity: isEntity ? RegisterEnum.Business : RegisterEnum.Individual,
    });

    if (resp.error) {
      switch (resp.error.code) {
        case ResponseCodes.ServerError:
          toast.error(err("500"));
          break;
        case ResponseCodes.UserIsNotExists:
          toast.error(err(String(resp.error.code)));
          break;
        default:
          toast.error(err("500"));
          break;
      }
    } else {
      setRegisterType(
        isEntity ? RegisterEnum.Business : RegisterEnum.Individual
      );
      setPhoneNumber(clearPhone);
      const { id } = resp.data.data;
      onboardModel.onboardStore.getState().setId(id);

      const status = resp.data.code;
      switch (status) {
        case ResponseCodes.Success:
          navigate(PATH_LIST.forgotPasswordVerify);
          break;
        case ResponseCodes.NotRegistered:
          toast.warning("Please register to continue");
          navigate(PATH_LIST.chooseAccType);
          break;
        default:
          break;
      }
    }

    setIsSubmitting(false);
  };

  return (
    <div className="forgetPasswordPage">
      <div className="forgot-password-text-wrapper">
        <p className="auth-title">{t("forgotYourPassword")}</p>
        <span className="enterPhoneSpan">{t("enterPhone")}</span>
      </div>

      <div className="forgot-password-form-type-wrapper">
        <IndividualBusinessToggle
          onIndividual={() => setIsEntity(false)}
          onBusiness={() => setIsEntity(true)}
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid) onRegister();
          }}
        >
          <label>{t("phoneNumber")}</label>

          <Suspense fallback={<div>{t("loading")}...</div>}>
            <LazyPhoneInput
              defaultCountry={"am"}
              value={phone}
              onChange={setPhone}
              inputProps={{ autoFocus: true }}
            />
          </Suspense>

          {!isValid && phone && (
            <p className="validation">{y("phoneNotValid")}</p>
          )}

          <Button
            className="phoneNumberBtn"
            children={t("submit")}
            variant="dark"
            disabled={!isValid || isSubmitting}
            type="submit"
          />
        </form>
      </div>
    </div>
  );
};
